import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonModal,
    IonIcon,
    IonCard,
    IonCardContent,
    IonGrid,
    IonRow,
    IonCol,
    IonLabel,
    IonText,
    IonSpinner,
} from "@ionic/react";
import { closeOutline, eyeOutline, alertCircleOutline } from "ionicons/icons";
import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    Box,
    Alert,
    TablePagination,
} from "@mui/material";
import axios from "axios"; // 1. Import Axios
import { getAppSettings, baseUrl } from "../../../common/utils"; // 2. Import baseUrl

/* ================= TYPES ================= */

interface ApiDiscrepancy {
    id: number;
    discrepancy_ref: string;
    delivery_id: string;
    product_id: string;
    type: string;
    quantity_affected: number;
    description: string;
    resolution_status: string;
    created_at: string;
    updated_at: string;
    vendor_id: string;
}

interface ApiResponse {
    total: number;
    data: ApiDiscrepancy[];
}

interface DiscrepancyReport {
    id: number;
    ref: string;
    deliveryId: string;
    type: string;
    status: string;
    date: string;
    description: string;
    quantity: number;
    productId: string;
}

/* ================= COMPONENT ================= */

const DiscrepancyDashboard = () => {
    const { siteName } = getAppSettings();

    // Data State
    const [reports, setReports] = useState<DiscrepancyReport[]>([]);
    const [selectedReport, setSelectedReport] = useState<DiscrepancyReport | null>(null);

    // UI State
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    /* ================= FETCH DATA (AXIOS) ================= */

    useEffect(() => {
        fetchReports();
    }, [page, rowsPerPage]);

    const fetchReports = async () => {
        try {
            setLoading(true);
            setError(null);

            const offset = page * rowsPerPage;

            // 3. Use Axios with baseUrl()
            const response = await axios.get<ApiResponse>(
                `${baseUrl()}discrepancy-report?limit=${rowsPerPage}&offset=${offset}`
            );

            // 4. Access data directly via response.data
            const json = response.data;

            // 1. Update Total Count for Pagination
            setTotalCount(json.total || 0);

            // 2. Map Data
            const apiData = json.data || [];
            const mappedReports: DiscrepancyReport[] = apiData.map((item) => ({
                id: item.id,
                ref: item.discrepancy_ref,
                deliveryId: item.delivery_id,
                type: item.type,
                status: item.resolution_status,
                date: item.created_at,
                description: item.description,
                quantity: item.quantity_affected,
                productId: item.product_id
            }));

            setReports(mappedReports);

        } catch (err: any) {
            console.error(err);
            // 5. Handle Axios errors safely
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch discrepancy reports";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    /* ================= HELPERS ================= */

    const getStatusColor = (status: string) => {
        const normalized = status.toLowerCase();
        if (normalized.includes("resolved") || normalized.includes("approved")) return "success";
        if (normalized.includes("rejected") || normalized.includes("closed")) return "error";
        if (normalized.includes("notified") || normalized.includes("pending")) return "warning";
        return "default";
    };

    const formatStatus = (status: string) => {
        return status.replace(/_/g, " ");
    };

    // --- Modal Handlers ---
    const handleOpenModal = (report: DiscrepancyReport) => {
        setSelectedReport(report);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReport(null);
    };

    // --- Pagination Handlers ---
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /* ================= RENDER ================= */

    return (
        <IonPage className="pushDown">
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle>{siteName || "Discrepancies"}</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="ion-padding">
                <Box sx={{ p: 2 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        Discrepancy Reports
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Paper
                        sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: "rgba(15,23,42,0.85)",
                            backdropFilter: "blur(6px)",
                            boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {loading && reports.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                                <IonSpinner name="crescent" />
                            </Box>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {["Discrepancy Ref", "Delivery ID", "Type", "Status", "Action"].map((header) => (
                                                    <TableCell
                                                        key={header}
                                                        sx={{
                                                            backgroundColor: "rgba(2,6,23,0.95)",
                                                            color: "#cbd5f5",
                                                            fontWeight: 600,
                                                            fontSize: "0.75rem",
                                                            textTransform: "uppercase",
                                                        }}
                                                        align={header === "Action" ? "center" : "left"}
                                                    >
                                                        {header}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {reports.length === 0 && !loading ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                                                        No reports found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                reports.map((report) => (
                                                    <TableRow
                                                        key={report.id}
                                                        hover
                                                        sx={{
                                                            transition: "background-color 0.2s ease",
                                                            "&:hover": { backgroundColor: "rgba(148,163,184,0.08)" },
                                                        }}
                                                    >
                                                        <TableCell sx={{ color: "#e5e7eb", fontWeight: 500 }}>
                                                            {report.ref}
                                                        </TableCell>

                                                        <TableCell sx={{ color: "#94a3b8" }}>
                                                            {report.deliveryId}
                                                        </TableCell>

                                                        <TableCell sx={{ color: "#e5e7eb" }}>
                                                            <Chip
                                                                icon={<IonIcon icon={alertCircleOutline} style={{ fontSize: 16 }} />}
                                                                label={report.type}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ color: '#cbd5f5', borderColor: 'rgba(203, 213, 245, 0.3)' }}
                                                            />
                                                        </TableCell>

                                                        <TableCell>
                                                            <Chip
                                                                label={formatStatus(report.status)}
                                                                color={getStatusColor(report.status) as any}
                                                                size="small"
                                                                sx={{ fontWeight: 500, borderRadius: "6px" }}
                                                            />
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <IonButton
                                                                fill="clear"
                                                                size="small"
                                                                onClick={() => handleOpenModal(report)}
                                                            >
                                                                <span style={{ marginRight: "6px", textTransform: 'capitalize' }}>See More</span>
                                                                <IonIcon icon={eyeOutline} />
                                                            </IonButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    sx={{
                                        color: "#cbd5f5",
                                        borderTop: "1px solid rgba(148,163,184,0.2)",
                                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                                            marginTop: "0",
                                            marginBottom: "0"
                                        },
                                        ".MuiTablePagination-selectIcon": {
                                            color: "#cbd5f5"
                                        }
                                    }}
                                />
                            </>
                        )}
                    </Paper>
                </Box>

                {/* ================= MODAL ================= */}

                <IonModal
                    isOpen={showModal}
                    onDidDismiss={handleCloseModal}
                    style={{
                        "--width": "90%",
                        "--max-width": "500px",
                        "--max-height": "70vh",
                        "--border-radius": "16px",
                    }}
                >
                    <IonHeader>
                        <IonToolbar>
                            <IonTitle>Report Details</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={handleCloseModal}>
                                    <IonIcon icon={closeOutline} />
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>

                    <IonContent className="ion-padding">
                        {selectedReport && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                                {/* Header Card */}
                                <IonCard sx={{ m: 0, borderRadius: 3 }}>
                                    <IonCardContent>
                                        <IonLabel color="medium">Reference ID</IonLabel>
                                        <Typography variant="h6" fontWeight="bold">
                                            {selectedReport.ref}
                                        </Typography>
                                        <IonGrid className="ion-no-padding ion-margin-top">
                                            <IonRow>
                                                <IonCol>
                                                    <IonLabel color="medium">Type</IonLabel>
                                                    <IonText color="danger">
                                                        <p style={{ margin: 0, fontWeight: 600 }}>{selectedReport.type}</p>
                                                    </IonText>
                                                </IonCol>
                                                <IonCol>
                                                    <IonLabel color="medium">Quantity Affected</IonLabel>
                                                    <p style={{ margin: 0, fontWeight: 600 }}>{selectedReport.quantity}</p>
                                                </IonCol>
                                            </IonRow>
                                        </IonGrid>
                                    </IonCardContent>
                                </IonCard>

                                {/* Date Section */}
                                <Box sx={{ px: 1 }}>
                                    <IonLabel color="primary">
                                        <h3>Report Date</h3>
                                    </IonLabel>
                                    <Typography variant="body1" sx={{ color: 'primary', mt: 0.5 }}>
                                        {selectedReport.date}
                                    </Typography>
                                </Box>

                                {/* Description Section */}
                                <Box sx={{ px: 1, pb: 2 }}>
                                    <IonLabel color="primary">
                                        <h3>Description</h3>
                                    </IonLabel>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            mt: 1,
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 2
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                            {selectedReport.description}
                                        </Typography>
                                    </Paper>
                                </Box>

                            </Box>
                        )}
                    </IonContent>
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default DiscrepancyDashboard;